/**
 * useGroupsData - Hook for Groups Management data access
 *
 * Key features:
 * - NO derived data computation (server provides everything)
 * - NO sorting (server returns pre-sorted)
 * - NO map building (use computed fields directly)
 *
 * The server returns groups with:
 * - section: "orphanedParents" | "grandparents"
 * - hierarchyLevel: 1 | 2 | 3 (1 = top-level, 2 = child, 3 = grandchild)
 * - hasChildren: boolean
 * - descendantIds: number[]
 * - canHaveSubgroups: boolean
 *
 * @module useGroupsData
 */

import { useMemo, useCallback } from "react";

import { useGroupsStore } from "../stores/groupsStore";
import { useGroups } from "../API/queries";

/**
 * Main hook for accessing groups data.
 *
 * Returns groups directly from React Query cache - no derived computation.
 * Server provides pre-sorted groups with all computed fields.
 *
 * @returns {Object} Groups data and query state
 */
export const useGroupsData = () => {
    const query = useGroups();

    // Groups are pre-sorted from server - no sorting needed
    // PERF: Memoize to prevent new array reference when data is undefined
    const groups = useMemo(
        () => query.data?.groups ?? [],
        [query.data?.groups]
    );

    // Build a simple lookup map for getGroupById helper
    // This is the ONLY computation we do on the client
    const groupMap = useMemo(() => {
        const map = {};
        groups.forEach((group) => {
            map[group.ID] = group;
        });
        return map;
    }, [groups]);

    // PERF: Memoize lookup function to prevent downstream re-renders
    const getGroupById = useCallback((id) => groupMap[id], [groupMap]);

    return {
        // Query state
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,

        // Groups data (pre-sorted from server)
        groups,

        // Lookup map (for VirtualizedGroupList)
        groupMap,

        // Simple lookup helper (stable reference)
        getGroupById,

    };
};

/**
 * Get visible groups (filtered by collapsed ancestors).
 * Combines React Query data with Zustand collapse state.
 *
 * Uses descendantIds from server instead of computing on client.
 */
export const useVisibleGroups = () => {
    const { groups, getGroupById } = useGroupsData();
    const collapsedGroups = useGroupsStore((state) => state.collapsedGroups);

    return useMemo(() => {
        if (collapsedGroups.size === 0) {
            // Fast path: nothing collapsed, return all groups
            return groups;
        }

        // Build set of hidden group IDs (descendants of collapsed groups)
        const hiddenIds = new Set();
        collapsedGroups.forEach((collapsedId) => {
            const collapsedGroup = getGroupById(collapsedId);
            if (collapsedGroup?.descendantIds) {
                collapsedGroup.descendantIds.forEach((id) => hiddenIds.add(id));
            }
        });

        // Filter out hidden groups
        return groups.filter((group) => !hiddenIds.has(group.ID));
    }, [groups, collapsedGroups, getGroupById]);
};


export default useGroupsData;
